import type { Map as LeafletMap } from "leaflet";
import type { RefObject } from "react";
import { useCallback, useState } from "react";
import { useTrackingStore } from "../store";
import type { RouteCoordinate, TripRouteData } from "../types";
import { useRoutePlayback } from "./use-route-playback";

export function useRouteSelection(
	mapRef: RefObject<LeafletMap | null>,
	removeRoutingControl: () => void
) {
	const {
		setRouteCoords,
		setRouteStartMarker,
		setRouteEndMarker,
		setPositionMarkers,
		setSelectedPositionInfo,
		setSelectedPositionIndex,
	} = useTrackingStore();

	const [replayPoints, setReplayPoints] = useState<RouteCoordinate[] | null>(
		null
	);
	const routePlayback = useRoutePlayback(replayPoints ?? []);

	const handleTripRouteSelect = useCallback(
		(routeData: TripRouteData, leafletCoords: [number, number][]) => {
			removeRoutingControl();

			setReplayPoints(null);
			setPositionMarkers([]);
			setSelectedPositionInfo(null);
			setSelectedPositionIndex(null);

			if (leafletCoords.length > 0) {
				setRouteCoords(routeData.coordinates);
				mapRef.current?.fitBounds(leafletCoords, { padding: [50, 50] });
			} else {
				setRouteCoords([]);
			}

			if (routeData.startMarker) {
				setRouteStartMarker({
					position: [routeData.startMarker.lat, routeData.startMarker.lng],
					address: routeData.startMarker.address,
					showCar: routeData.startMarker.showCar ?? false,
					speed: routeData.startMarker.speed,
					dateTelemetry: routeData.startMarker.dateTelemetry,
				});
			} else {
				setRouteStartMarker(null);
			}

			if (routeData.endMarker) {
				setRouteEndMarker({
					position: [routeData.endMarker.lat, routeData.endMarker.lng],
					address: routeData.endMarker.address,
					speed: routeData.endMarker.speed,
					dateTelemetry: routeData.endMarker.dateTelemetry,
				});
			} else {
				setRouteEndMarker(null);
			}
		},
		[
			mapRef,
			removeRoutingControl,
			setPositionMarkers,
			setRouteCoords,
			setRouteEndMarker,
			setRouteStartMarker,
			setSelectedPositionInfo,
			setSelectedPositionIndex,
		]
	);

	const handleReplayRouteSelect = useCallback(
		(routeData: TripRouteData, leafletCoords: [number, number][]) => {
			removeRoutingControl();

			setPositionMarkers([]);
			setSelectedPositionInfo(null);
			setSelectedPositionIndex(null);
			setRouteStartMarker(null);
			setRouteEndMarker(null);
			setRouteCoords(routeData.coordinates);
			setReplayPoints(routeData.coordinates);

			if (leafletCoords.length > 0 && mapRef.current) {
				mapRef.current.fitBounds(leafletCoords, { padding: [50, 50] });
			}
		},
		[
			mapRef,
			removeRoutingControl,
			setPositionMarkers,
			setRouteCoords,
			setRouteEndMarker,
			setRouteStartMarker,
			setSelectedPositionInfo,
			setSelectedPositionIndex,
		]
	);

	const handleRouteSelect = useCallback(
		(routeData: TripRouteData) => {
			const leafletCoords: [number, number][] = routeData.coordinates.map(
				(c) => [c.lat, c.lng]
			);

			if (routeData.isReplay) {
				handleReplayRouteSelect(routeData, leafletCoords);
				return;
			}

			handleTripRouteSelect(routeData, leafletCoords);
		},
		[handleTripRouteSelect, handleReplayRouteSelect]
	);

	const handleClosePlayback = useCallback(() => {
		setReplayPoints(null);
		setRouteCoords([]);
	}, [setRouteCoords]);

	const clearReplay = useCallback(() => {
		setReplayPoints(null);
	}, []);

	return {
		replayPoints,
		routePlayback,
		handleRouteSelect,
		handleClosePlayback,
		clearReplay,
	};
}
